import React, { useEffect, useState } from 'react';
import { Button, Modal, Select, Space, Table, Typography, message } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

const RoleAssign = () => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [roleResp, userResp] = await Promise.all([
        authAPI.roles(),
        authAPI.users(),
      ]);
      const roleList = roleResp.data || [];
      const userList = userResp.data || [];
      setRoles(roleList);
      setUsers(userList);

      const nextEditing = {};
      userList.forEach((u) => {
        nextEditing[u.id] = u.role_name || 'LOGIN_USER';
      });
      setEditing(nextEditing);
    } catch (e) {
      message.error(e?.response?.data?.detail || '获取用户/角色失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleSave = (record) => {
    const uid = record?.id;
    if (!uid) return;

    const nextRole = editing?.[uid] || 'LOGIN_USER';

    Modal.confirm({
      title: '确认修改角色',
      content: (
        <div>
          <div>{`确定要将用户“${record.username || uid}”的角色修改为 “${nextRole}” 吗？`}</div>
          <div style={{ color: 'red', marginTop: 8 }}>
            此操作会覆盖该用户原有角色（系统约定：一人仅一个角色）。
          </div>
        </div>
      ),
      okText: '确认修改',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setSavingUserId(uid);
        try {
          const resp = await authAPI.setUserRole(uid, nextRole);
          if (resp?.data?.ok) {
            message.success('修改成功');
          } else {
            message.error('修改失败');
          }
          await fetchAll();
        } catch (e) {
          message.error(e?.response?.data?.detail || '修改失败');
        } finally {
          setSavingUserId(null);
        }
      },
    });
  };

  const roleOptions = (roles || []).map((r) => ({
    label: r.name,
    value: r.name,
  }));

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: '用户名', dataIndex: 'username', key: 'username', width: 180 },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 220, render: (v) => v || '-' },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (v) => (v ? '是' : '否'),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (v) => (v ? moment.utc(v).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '角色',
      key: 'role',
      width: 200,
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          options={roleOptions}
          value={editing?.[record.id] || record.role_name || 'LOGIN_USER'}
          onChange={(val) => setEditing((prev) => ({ ...prev, [record.id]: val }))}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Button
          type="link"
          icon={<SaveOutlined />}
          loading={savingUserId === record.id}
          onClick={() => handleSave(record)}
        >
          保存
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>权限管理</Title>
          <Text type="secondary">管理员可以为用户分配角色（系统约定：一人仅一个角色）。</Text>
        </div>

        <div>
          <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>
            刷新
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20 }}
        />
      </Space>
    </div>
  );
};

export default RoleAssign;
